/**
 * Cascade Prediction + What-If Preview (Deterministic)
 *
 * Pure logic — no side effects, no API calls, no AI.
 * Computes projected downstream delay impact and "virtual move" diffs.
 *
 * Performance: all helpers are O(n) where n = todaySchedule.length.
 */

import type { Appointment } from '../types/appointments';
import { SLA_WARN_MINUTES } from './sla';

// ─── Safe Date Parser ─────────────────────────────────────────────────────────

/** Returns epoch ms or NaN. Never throws. */
export function safeDateMs(iso?: string | null): number {
    if (!iso) return NaN;
    const ms = new Date(iso).getTime();
    return Number.isFinite(ms) ? ms : NaN;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CascadeSummary {
    countDelayed: number;
    avgDelayMinutes: number;
    maxDelayMinutes: number;
    atRiskCount: number;
    impactedRows: CascadeRow[];
}

export interface CascadeRow {
    appointment: Appointment;
    predictedDelayMinutes: number;
    isAtRisk: boolean;
    atRiskReason?: string; // e.g. '≥30m projected delay' or 'High risk flag'
}

export interface WhatIfDiff {
    label: string; // e.g. "Preview: 2:30 PM → Dock 4"
    blocked: boolean;
    blockReason?: string;
    warnings: string[];
    /** false when time data was invalid and metrics are not trustworthy */
    metricsValid: boolean;
    beforeDelayed: number;
    afterDelayed: number;
    beforeAtRisk: number;
    afterAtRisk: number;
    beforeStacking: number;
    afterStacking: number;
    verdict: 'improvement' | 'neutral' | 'worse' | 'risk';
}

// ─── Cascade Prediction ───────────────────────────────────────────────────────

const DECAY_PER_SLOT = 10; // minutes of buffer decay per queued slot
const AT_RISK_DELAY_THRESHOLD = 30; // minutes

/**
 * Compute projected downstream impact for a breached dock.
 * Uses a simple queue-delay model: each subsequent appointment
 * inherits delay minus a 10m buffer decay per slot.
 */
export function computeCascade(
    dockId: string,
    dwellMinutes: number,
    todaySchedule: Appointment[],
    now: number, warehouseTimeZone: string,
): CascadeSummary {
    const currentDelay = Math.max(0, dwellMinutes - SLA_WARN_MINUTES);
    const threeHoursMs = 3 * 60 * 60 * 1000;
    const cutoff = now + threeHoursMs;

    // Upcoming appointments on the breached dock
    const upcoming = todaySchedule
        .filter(a => {
            if (!['approved', 'scheduled'].includes(a.status)) return false;
            const did = a.dockId || a.dock_id;
            if (did !== dockId) return false;
            const startMs = safeDateMs(a.startAt);
            return !isNaN(startMs) && startMs >= now && startMs <= cutoff;
        })
        .sort((a, b) => safeDateMs(a.startAt) - safeDateMs(b.startAt));

    if (upcoming.length === 0 || currentDelay <= 0) {
        return { countDelayed: 0, avgDelayMinutes: 0, maxDelayMinutes: 0, atRiskCount: 0, impactedRows: [] };
    }

    const rows: CascadeRow[] = [];
    let totalDelay = 0;
    let maxDelay = 0;
    let atRisk = 0;

    for (let i = 0; i < upcoming.length; i++) {
        const delay = Math.max(0, currentDelay - DECAY_PER_SLOT * i);
        if (delay <= 0) break;

        const isAtRiskByDelay = delay >= AT_RISK_DELAY_THRESHOLD;
        const isAtRiskByLevel = upcoming[i].delayRiskLevel === 'High';
        const isAtRisk = isAtRiskByDelay || isAtRiskByLevel;
        const atRiskReason = isAtRisk
            ? (isAtRiskByDelay ? '≥30m projected delay' : 'High risk flag')
            : undefined;

        rows.push({
            appointment: upcoming[i],
            predictedDelayMinutes: Math.round(delay),
            isAtRisk,
            atRiskReason,
        });

        totalDelay += delay;
        maxDelay = Math.max(maxDelay, delay);
        if (isAtRisk) atRisk++;
    }

    return {
        countDelayed: rows.length,
        avgDelayMinutes: rows.length > 0 ? Math.round(totalDelay / rows.length) : 0,
        maxDelayMinutes: Math.round(maxDelay),
        atRiskCount: atRisk,
        impactedRows: rows.slice(0, 3),
    };
}

// ─── Stacking Count Helper ────────────────────────────────────────────────────

export function countStackingOnDock(
    dockId: string,
    todaySchedule: Appointment[],
    now: number, warehouseTimeZone: string,
): number {
    const threeHoursMs = 3 * 60 * 60 * 1000;
    const cutoff = now + threeHoursMs;
    const WINDOW_MS = 45 * 60 * 1000;

    const upcoming = todaySchedule
        .filter(a => {
            if (!['approved', 'scheduled'].includes(a.status)) return false;
            const did = a.dockId || a.dock_id;
            if (did !== dockId) return false;
            const t = safeDateMs(a.startAt);
            return !isNaN(t) && t >= now && t <= cutoff;
        })
        .map(a => safeDateMs(a.startAt))
        .sort((a, b) => a - b);

    // Count pairs within 45m window
    let conflicts = 0;
    for (let i = 0; i < upcoming.length; i++) {
        for (let j = i + 1; j < upcoming.length; j++) {
            if (upcoming[j] - upcoming[i] <= WINDOW_MS) {
                conflicts++;
            } else {
                break;
            }
        }
    }
    return conflicts;
}

// ─── What-If Preview ──────────────────────────────────────────────────────────

/**
 * Count overlapping appointments on a dock within a specific time window.
 * Used for guardrail checks (target dock feasibility).
 */
export function countWindowOverlaps(
    dockId: string,
    windowStartMs: number,
    windowEndMs: number,
    todaySchedule: Appointment[],
): number {
    return todaySchedule.filter(a => {
        const did = a.dockId || a.dock_id;
        if (did !== dockId) return false;
        if (!['approved', 'scheduled', 'checked_in', 'in_progress'].includes(a.status)) return false;
        const aStart = safeDateMs(a.startAt);
        const aEnd = safeDateMs(a.endAt);
        if (isNaN(aStart) || isNaN(aEnd)) return false;
        return aStart < windowEndMs && aEnd > windowStartMs;
    }).length;
}

/**
 * Simulate moving an appointment from one dock to another.
 * Returns a compact before/after diff with guardrails — no data mutation.
 */
export function computeWhatIf(
    appointment: Appointment,
    fromDockId: string,
    _fromDockNumber: string,
    toDockId: string,
    toDockNumber: string,
    _fromDockType: string | undefined,
    _toDockType: string | undefined,
    dwellMinutes: number,
    todaySchedule: Appointment[],
    now: number, warehouseTimeZone: string,
): WhatIfDiff {
    const time = formatTimeCompact(appointment.startAt, warehouseTimeZone);
    const label = `Preview: ${time} → Dock ${toDockNumber}`;
    const warnings: string[] = [];
    const blockedResult = (reason: string): WhatIfDiff => ({
        label, blocked: true, blockReason: reason,
        warnings: [], metricsValid: false, beforeDelayed: 0, afterDelayed: 0,
        beforeAtRisk: 0, afterAtRisk: 0,
        beforeStacking: 0, afterStacking: 0, verdict: 'neutral',
    });

    // ── Guardrail: no-op move ─────────────────────────────────────
    if (fromDockId === toDockId) {
        return blockedResult('Already assigned to this dock');
    }

    // ── Guardrail: target dock overlap check ──────────────────────
    const startMs = safeDateMs(appointment.startAt);
    const endMs = safeDateMs(appointment.endAt);
    const windowValid = !isNaN(startMs) && !isNaN(endMs);
    let hasTargetOverlaps = false;

    if (!windowValid) {
        warnings.push('Time invalid');
    } else {
        const targetOverlaps = countWindowOverlaps(toDockId, startMs, endMs, todaySchedule);
        if (targetOverlaps > 0) {
            hasTargetOverlaps = true;
            warnings.push(`${targetOverlaps} overlap${targetOverlaps !== 1 ? 's' : ''} on target`);
        }
    }

    // ── Compute before/after (apples-to-apples: both docks on both sides) ──
    // Cascade: combined scope — from dock (with breach dwell) + to dock (no breach, dwell=0)
    const beforeCascadeFrom = computeCascade(fromDockId, dwellMinutes, todaySchedule, now, warehouseTimeZone);
    const beforeCascadeTo = computeCascade(toDockId, 0, todaySchedule, now, warehouseTimeZone);
    const beforeDelayed = beforeCascadeFrom.countDelayed + beforeCascadeTo.countDelayed;
    const beforeAtRisk = beforeCascadeFrom.atRiskCount + beforeCascadeTo.atRiskCount;

    // Stacking: same scope on both sides — fromDock + toDock
    const beforeStackFrom = countStackingOnDock(fromDockId, todaySchedule, now, warehouseTimeZone);
    const beforeStackTo = countStackingOnDock(toDockId, todaySchedule, now, warehouseTimeZone);
    const beforeStacking = beforeStackFrom + beforeStackTo;

    const virtualSchedule = todaySchedule.map(a =>
        a.id === appointment.id
            ? { ...a, dockId: toDockId, dock_id: toDockId }
            : a
    );

    const afterCascadeFrom = computeCascade(fromDockId, dwellMinutes, virtualSchedule, now, warehouseTimeZone);
    const afterCascadeTo = computeCascade(toDockId, 0, virtualSchedule, now, warehouseTimeZone);
    const afterDelayed = afterCascadeFrom.countDelayed + afterCascadeTo.countDelayed;
    const afterAtRisk = afterCascadeFrom.atRiskCount + afterCascadeTo.atRiskCount;

    const afterStackFrom = countStackingOnDock(fromDockId, virtualSchedule, now, warehouseTimeZone);
    const afterStackTo = countStackingOnDock(toDockId, virtualSchedule, now, warehouseTimeZone);
    const afterStacking = afterStackFrom + afterStackTo;

    // Warn if target dock has predicted stacking (only when window is valid and no direct overlaps)
    if (windowValid && afterStackTo > 0 && !hasTargetOverlaps) {
        warnings.push('Target dock congestion risk');
    }

    // ── Verdict ──────────────────────────────────────────────────
    const delayDelta = afterDelayed - beforeDelayed;
    const stackDelta = afterStacking - beforeStacking;
    let verdict: 'improvement' | 'neutral' | 'worse' | 'risk' = 'neutral';

    if (hasTargetOverlaps) {
        verdict = 'risk';
    } else if (delayDelta < 0 || stackDelta < 0) {
        verdict = 'improvement';
    } else if (delayDelta > 0 || stackDelta > 0) {
        verdict = 'worse';
    }

    return {
        label,
        blocked: false,
        warnings,
        metricsValid: windowValid,
        beforeDelayed: Math.max(0, beforeDelayed),
        afterDelayed: Math.max(0, afterDelayed),
        beforeAtRisk: Math.max(0, beforeAtRisk),
        afterAtRisk: Math.max(0, afterAtRisk),
        beforeStacking: Math.max(0, beforeStacking),
        afterStacking: Math.max(0, afterStacking),
        verdict,
    };
}

import { formatTimeInTimeZone } from './timezoneDisplay';


function formatTimeCompact(iso: string, warehouseTimeZone: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    

    
    return formatTimeInTimeZone(iso, warehouseTimeZone);
}
